import {
  profile_boy,
  profile_female,
  profile_girl,
  profile_male,
  profile_none,
} from 'components/base/images';
import AppModal from 'components/common/modal/AppModal';
import AlertModalContent from 'components/common/modal/contents/AlertModalContent';
import PlainModal from 'components/common/modal/PlainModal';
import Patient from 'components/patient/Patient';
import ProjectListContainer from 'containers/project/clinic/ProjectListContainer';
import { calculateAge } from 'lib/library';
import { pageUrl } from 'lib/mapper';
import { IPatientInfo } from 'lib/types/patientTypes';
import { useDidUpdateEffect } from 'lib/utils';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { AppActions, PatientActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import styled from 'styled-components';
import DeletePatientModalContainer from './DeletePatientModalContainer';
import PatientModalContainer from './PatientModalContainer';

interface IPatient {}

function PatientContainer() {
  const {
    language,
    patientData,
    fetchPatientSuccess,
    editPatientSuccess,
    deletePatientSuccess,
    deletePatientFailure,
    projectsData: { list: projects, pagingData: projectsPagingData },
    projectsSuccess,
    deleteProjectSuccess,
  } = useShallowAppSelector(state => ({
    language: state.base.language,
    patientData: state.patient.patient.data,
    fetchPatientSuccess: state.patient.patient.success,
    editPatientSuccess: state.patient.editPatient.success,
    deletePatientSuccess: state.patient.deletePatient.success,
    deletePatientFailure: state.patient.deletePatient.failure,
    projectsData: state.project.projects.data || {},
    projectsSuccess: state.project.projects.success,
    deleteProjectSuccess: state.project.deleteProject.success,
  }));
  const { ptcode: patientCode } = useParams() as { ptcode: string };
  const { ipcRenderer } = window;
  const { t } = useTranslation();
  const history = useHistory();
  const hasAlarm = false;
  const [modifyPatientModalOpen, setModifyPatientModalOpen] = useState(false);
  const handleCloseModifyPatientModal = () => setModifyPatientModalOpen(false);
  const handleOpenModifyPatientModal = () => setModifyPatientModalOpen(true);

  const [deletePatientModalOpen, setDeletePatientModalOpen] = useState(false);
  const handleCloseDeletePatientModal = () => setDeletePatientModalOpen(false);
  const handleOpenDeletePatientModal = () => setDeletePatientModalOpen(true);

  const patientInfo: IPatientInfo = patientData?.patientInfo || {};
  const [profileFilePreview, setProfileFilePreview] = useState<object | any>(null);

  useEffect(() => {
    PatientActions.fetch_patient_request(patientCode);
    return () => {
      PatientActions.fetch_patient_clear();
    };
  }, []);

  useDidUpdateEffect(() => {
    if (!fetchPatientSuccess) return;
  }, [fetchPatientSuccess]);

  useDidUpdateEffect(() => {
    if (!editPatientSuccess) return;
    PatientActions.fetch_patient_request(patientCode);
  }, [editPatientSuccess]);

  useDidUpdateEffect(() => {
    if (deletePatientSuccess) {
      handleCloseDeletePatientModal();
      handleOpenDeleteCompleteModal();
    }
  }, [deletePatientSuccess]);

  useDidUpdateEffect(() => {
    if (patientInfo) {
      if (!patientInfo.profileImgIdx) {
        // NOTE: 프로필 이미지 없는경우 디폴트 이미지
        const patientAge = calculateAge(patientInfo.birthday);
        if (patientAge.isAdult) {
          if (patientInfo.gender === 'F') {
            setProfileFilePreview(profile_female);
          } else if (patientInfo.gender === 'M') {
            setProfileFilePreview(profile_male);
          } else {
            setProfileFilePreview(profile_none);
          }
        } else {
          if (patientInfo.gender === 'F') {
            setProfileFilePreview(profile_girl);
          } else if (patientInfo.gender === 'M') {
            setProfileFilePreview(profile_boy);
          } else {
            setProfileFilePreview(profile_none);
          }
        }
      } else {
        // TODO: 프로필 이미지있는 경우, 프로필이미지 다운로드 추가 예정
        // setProfileFilePreview()
      }
    }
  }, [patientInfo]);

  const handleCloseModal = () => {
    setModifyPatientModalOpen(false);
  };

  const handleGoToBack = () => {
    history.push(pageUrl.patient.list);
  };

  const handleOpenDeleteCompleteModal = () => {
    AppActions.add_popup({
      isOpen: true,
      timeout: 0,
      isCloseIcon: true,
      title: '',
      okText: t(`GLOBAL_CONFIRM`),
      isCloseIconButton: false,
      content: (
        <AlertModalContent
          title={t(`PATIENT_DELETE_COMPLETE_TITLE`)}
          mainText={t(`PATIENT_DELETE_COMPLETE_CONTENTS`)}
          subText={''}
        />
      ),
      onClick: () => {
        handleGoToBack();
      },
    });
  };

  return (
    <>
      <PlainModal
        isOpen={deletePatientModalOpen}
        onClose={handleCloseDeletePatientModal}
        width={570}
      >
        <AppModal
          title={'Patient'}
          content={<DeletePatientModalContainer patientInfo={patientInfo} />}
          contentCardStyle={{
            padding: '0px',
            backgroundColor: 'transparent',
          }}
          isCloseIcon={true}
          onClick={handleCloseDeletePatientModal}
          onCancel={handleCloseDeletePatientModal}
          hideButton={true}
        />
      </PlainModal>
      <PlainModal
        isOpen={modifyPatientModalOpen}
        onClose={handleCloseModifyPatientModal}
        width={1000}
      >
        <AppModal
          title={'Patient'}
          content={
            <PatientModalContainer
              type="edit"
              onCloseModal={handleCloseModal}
              ptCode={patientCode}
            />
          }
          contentCardStyle={{
            padding: '0px',
            backgroundColor: 'transparent',
          }}
          isCloseIcon={true}
          onClick={handleCloseModifyPatientModal}
          onCancel={handleCloseModifyPatientModal}
          hideButton={true}
        />
      </PlainModal>
      <StyledPatient data-component-name="Patient">
        <Patient
          language={language}
          hasAlarm={hasAlarm}
          patientInfo={patientInfo}
          onOpenDeletePatientModal={handleOpenDeletePatientModal}
          onOpenModifyPatientModal={handleOpenModifyPatientModal}
          profileFilePreview={profileFilePreview}
        />
        <ProjectListContainer patientCode={patientCode} />
      </StyledPatient>
    </>
  );
}

const StyledPatient = styled.section<{}>`
  display: flex;
  flex-direction: column;
  row-gap: 20px;
`;

export default React.memo(PatientContainer);
